import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {ObjectTypes, ApiErrorCodes, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import {ServiceDomainGetListSchema} from '../../../api/DBSchema/ServiceDomain';
import store from '../../../store';
import EditObjectCell from '../../../components/core/SMCTable/CommonCells/EditObjectCell';
import greenCheckLogo from '../../../assets/images/green_check.png';
import redXLogo from '../../../assets/images/red_x.png';
import mailLogo from '../../../assets/images/mail.png';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import SMCLink from '../../../components/Simple/SMCLink';
import RowLayout from '../../../components/Layouts/RowLayout';
import {tryToRefreshAvailablePortals} from '../../../store/actions/user.actions';
import {buildBooleanSort, buildStringSortCaseInsensitive, buildNumberSort} from '../../../utils/sortUtils';


const emailCell = React.memo(({value}) => {
    if (value) {
        return <SMCLink inline href={`mailto:${value}`}><RowLayout spacing="2px" wrap="nowrap"><img src={mailLogo} alt="email" /> {value}</RowLayout></SMCLink>
    } else {
        return null;
    }
});

const enabledCell = React.memo(({value}) => {
    if (value === true) {
        return <img src={greenCheckLogo} alt="enabled" />
    } else if (value === false) {
        return <img src={redXLogo} alt="disabled" />
    } else {
        return null;
    }
});

export function useServiceDomainResults() {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { sortBasis, sortDirection, setSort } = useSort('name', SortDirections.ASCENDING);

    const columns = useMemo(() => ([
        { id: 'name',        accessor: ServiceDomainGetListSchema.name,        header: t('SMP.NAME'),                               width: 135,                  defaultShow: true, sort: buildStringSortCaseInsensitive(ServiceDomainGetListSchema.name),        Cell: EditObjectCell    },
        { id: 'description', accessor: ServiceDomainGetListSchema.description, header: t('SMP.DESCRIPTION'),                        width: 135,                  defaultShow: true, sort: buildStringSortCaseInsensitive(ServiceDomainGetListSchema.description),                         },
        { id: 'serverGroup', accessor: ServiceDomainGetListSchema.serverGroup, header: t('SMP.SERVICE_DOMAINS.TABLE.SERVER_GROUP'), width: 130,                  defaultShow: true, sort: buildStringSortCaseInsensitive(ServiceDomainGetListSchema.serverGroup),                         },
        { id: 'company',     accessor: ServiceDomainGetListSchema.company,     header: t('SMP.SERVICE_DOMAINS.TABLE.COMPANY'),      width: 135,                  defaultShow: true, sort: buildStringSortCaseInsensitive(ServiceDomainGetListSchema.company),                             },
        { id: 'contact',     accessor: ServiceDomainGetListSchema.contact,     header: t('SMP.SERVICE_DOMAINS.TABLE.CONTACT'),      width: 130,                  defaultShow: true, sort: buildStringSortCaseInsensitive(ServiceDomainGetListSchema.contact),                             },
        { id: 'email',       accessor: ServiceDomainGetListSchema.email,       header: t('SMP.SERVICE_DOMAINS.TABLE.EMAIL'),        width: 200,                  defaultShow: true, sort: buildStringSortCaseInsensitive(ServiceDomainGetListSchema.email),       Cell: emailCell         },
        { id: 'enabled',     accessor: ServiceDomainGetListSchema.enabled,     header: t('SMC_GEN_ENABLED'),                        width: 66,  align: 'center', defaultShow: true, sort: buildBooleanSort(ServiceDomainGetListSchema.enabled),                   Cell: enabledCell       },
        { id: 'tenantId',     accessor: ServiceDomainGetListSchema.tenantId,   header: t('SMP.SERVICE_DOMAINS.TABLE.TENANT_ID'),   width: 270,                  defaultShow: true, sort: buildStringSortCaseInsensitive(ServiceDomainGetListSchema.tenantId),                            },
        { id: 'customHeartbeatInterval', accessor: ServiceDomainGetListSchema.customHeartbeatInterval, header: t('SMP.SERVICE_DOMAINS.TABLE.CUSTOM_HEARTBEAT_INTERVAL'),  width: 190, defaultShow: true, sort: buildNumberSort(ServiceDomainGetListSchema.customHeartbeatInterval),    Cell: ({value}) => value },

    ]), [t]);
    
    const rowIdSelector = obj => obj[ServiceDomainGetListSchema.name];

    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.SERVICE_DOMAIN, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.SERVICE_DOMAIN, null);

    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const isFirstRender = useRef(true);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        isLastPage,
        reloadTable,
        reloadTableNoClearSelected,
        filterObject,
        setFilterObject,
        freeTextSearch,
        selectedObjects,
        setSelectedObjects,
        deleteSelectedObjects
    } = useGenericResultsDataApi(ObjectTypes.SERVICE_DOMAIN);


    const newObjectCallback = useCallback((objectId, errorCode) => {
        if (errorCode) {
            alert(t(errorCode));
        } else if (objectId) {
            store.dispatch(navigateTo({search: 'id=' + objectId}));
            store.dispatch(tryToRefreshAvailablePortals());
        }
    }, [t]);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const deleteCallback = useCallback((password) => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        setIsPageLoaded(false);
        deleteSelectedObjects(password, 30000)
            .then(({successMessage, errorMessage}) => {
                successMessage && enqueueSuccessSnackbar(successMessage);
                errorMessage && enqueueErrorSnackbar(errorMessage);
                reloadTable();
                store.dispatch(tryToRefreshAvailablePortals());
            })
            .catch(e => {
                const errorMessage = getErrorMessage(e);
                enqueueErrorSnackbar(errorMessage);
            })
            .finally(() => {
                setIsPageLoaded(true);
            });
    }, [selectedObjects, deleteSelectedObjects, reloadTable, enqueueSuccessSnackbar, enqueueErrorSnackbar, getErrorMessage, t]);

    // Reload table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable, enqueueErrorSnackbar, t]);

    // Reload table without clearing selected on filter changed
    useEffect(() => {
        // Don't run on first render, only when filter is changed
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        reloadTableNoClearSelected()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTableNoClearSelected, filterObject, enqueueErrorSnackbar, t]);

    return ({
        tableContent,
        isFetching,
        isPageLoaded,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        newObjectCallback,
        reloadButtonOnClick,
        deleteCallback,
        filterObject,
        setFilterObject,
        freeTextSearch
    });
}